<!-- 商品发布 - 基础设置 -->
<template>
  <el-form ref="formRef" :disabled="isDetail" :model="formData" :rules="rules" label-width="120px">
    <el-form-item v-if="!poolMode" label="所属店铺" prop="shopId">
      <el-select
        v-model="formData.shopId"
        class="w-80!"
        clearable
        filterable
        :disabled="isDetail || shopLocked"
        :placeholder="shopSelectPlaceholder"
      >
        <el-option
          v-for="item in shopOptions"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>
      <el-button :icon="RefreshRight" class="ml-1" size="small" @click="refreshShopList" />
      <div v-if="shopLocked" class="text-12px text-[var(--el-text-color-secondary)] mt-4px w-80!">
        第三方入驻商品不可更改所属店铺
      </div>
      <div
        v-else-if="shopOptions.length === 0"
        class="text-12px text-[var(--el-color-warning)] mt-4px w-80!"
      >
        暂无自营店铺。若当前为自营模式，请刷新页面由系统自动创建；或切到平台视角在「店铺管理」新增后再发品。
      </div>
      <div
        v-else-if="shopOptions.length === 1 && !shopLocked"
        class="text-12px text-[var(--el-text-color-secondary)] mt-4px w-80!"
      >
        已自动选中唯一自营店
      </div>
    </el-form-item>
    <el-form-item label="商品名称" prop="name">
      <el-input
        v-model="formData.name"
        :autosize="{ minRows: 2, maxRows: 2 }"
        :clearable="true"
        :show-word-limit="true"
        class="w-80!"
        maxlength="64"
        placeholder="请输入商品名称"
        type="textarea"
      />
    </el-form-item>
    <el-form-item label="商品分类" prop="categoryId">
      <el-cascader
        v-model="formData.categoryId"
        :options="categoryList"
        :props="defaultProps"
        class="w-80!"
        clearable
        filterable
        placeholder="请选择商品分类"
      />
      <el-button :icon="RefreshRight" @click="refreshCategoryList" class="ml-1" size="small" />
    </el-form-item>
    <el-form-item label="商品品牌" prop="brandId">
      <el-select v-model="formData.brandId" class="w-80!" placeholder="请选择商品品牌">
        <el-option
          v-for="item in brandList"
          :key="item.id"
          :label="item.name"
          :value="item.id as number"
        />
      </el-select>
      <el-button :icon="RefreshRight" @click="refreshBrandList" class="ml-1" size="small" />
    </el-form-item>
    <el-form-item v-if="!poolMode" label="货源渠道" prop="sourceChannel">
      <el-select
        v-model="formData.sourceChannel"
        class="w-80!"
        clearable
        placeholder="请选择货源渠道（可选）"
        @change="onSourceChannelChange"
      >
        <el-option
          v-for="item in SourceChannelOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <div class="text-12px text-[var(--el-text-color-secondary)] mt-4px w-80!">
        仅台账记录，不对接平台开放接口；选渠道须填链接。供方商品请走货品池选品挂店。
      </div>
    </el-form-item>
    <el-form-item v-if="!poolMode" label="货源链接" prop="sourceUrl">
      <el-input
        v-model="formData.sourceUrl"
        class="w-80!"
        clearable
        :disabled="!formData.sourceChannel"
        :placeholder="sourceUrlPlaceholder"
        maxlength="500"
        show-word-limit
      />
    </el-form-item>
    <el-form-item label="商品关键字" prop="keyword">
      <el-input v-model="formData.keyword" class="w-80!" placeholder="请输入商品关键字" />
    </el-form-item>
    <el-form-item label="商品简介" prop="introduction">
      <el-input
        v-model="formData.introduction"
        :autosize="{ minRows: 2, maxRows: 2 }"
        :clearable="true"
        :show-word-limit="true"
        class="w-80!"
        maxlength="128"
        placeholder="请输入商品简介"
        type="textarea"
      />
    </el-form-item>
    <el-form-item label="商品封面图" prop="picUrl">
      <UploadImg v-model="formData.picUrl" :disabled="isDetail" directory="mall/product" height="80px" />
    </el-form-item>
    <el-form-item label="商品轮播图" prop="sliderPicUrls">
      <UploadImgs v-model="sliderPicUrls" :disabled="isDetail" directory="mall/product" />
    </el-form-item>
  </el-form>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import { copyValueToTarget } from '@/utils'
import { propTypes } from '@/utils/propTypes'
import { defaultProps, handleTree } from '@/utils/tree'
import type { Spu } from '@/api/mall/product/spu'
import * as ProductCategoryApi from '@/api/mall/product/category'
import { CategoryVO } from '@/api/mall/product/category'
import * as ProductBrandApi from '@/api/mall/product/brand'
import { BrandVO } from '@/api/mall/product/brand'
import { getShopSimpleList } from '@/api/merchant/shop-manager'
import {
  ensureDefaultSelfOperatedShops,
  pickDefaultSelfOperatedShopId
} from '@/utils/mallSelfOperatedShop'
import { RefreshRight } from '@element-plus/icons-vue'

/** 货源渠道（台账，与履约来源/供应商无关） */
const SourceChannelOptions = [
  { label: '京东', value: 1 },
  { label: '天猫', value: 2 },
  { label: '淘宝', value: 3 },
  { label: '拼多多', value: 4 },
  { label: '1688', value: 5 },
  { label: '抖音电商', value: 6 },
  { label: '其他', value: 9 }
]

defineOptions({ name: 'ProductSpuInfoForm' })
const props = defineProps({
  propFormData: {
    type: Object as PropType<Spu>,
    default: () => {}
  },
  isDetail: propTypes.bool.def(false), // 是否作为详情组件
  /** 货品池代供：隐藏店铺与货源台账 */
  poolMode: propTypes.bool.def(false)
})

const message = useMessage() // 消息弹窗
const poolMode = computed(() => props.poolMode)

/** 平台自营 shop_type=1 */
const SELF_OPERATED = 1
/** 第三方入驻 shop_type=0 */
const THIRD_PARTY = 0

const formRef = ref() // 表单 Ref
const formData = reactive<Spu>({
  shopId: undefined, // 所属店铺
  name: '', // 商品名称
  categoryId: undefined, // 商品分类
  keyword: '', // 关键字
  picUrl: '', // 商品封面图
  sliderPicUrls: [], // 商品轮播图
  introduction: '', // 商品简介
  brandId: undefined, // 商品品牌
  // 履约字段不展示：编辑时从详情带回，避免清空选品挂店的外发供方
  productSource: undefined,
  supplierId: undefined,
  sourceChannel: undefined,
  sourceUrl: undefined
})
const sliderPicUrls = computed({
  get: () => formData.sliderPicUrls || [],
  set: (value: string[]) => {
    formData.sliderPicUrls = value
  }
})
const rules = computed(() => ({
  ...(props.poolMode ? {} : { shopId: [required] }),
  name: [required],
  categoryId: [required],
  keyword: [required],
  introduction: [required],
  picUrl: [required],
  sliderPicUrls: [required],
  brandId: [required],
  ...(props.poolMode
    ? {}
    : {
        sourceUrl: [
          {
            validator: (_rule: any, value: any, callback: any) => {
              if (!formData.sourceChannel) {
                callback()
                return
              }
              const url = typeof value === 'string' ? value.trim() : ''
              if (!url) {
                callback(new Error('选择货源渠道后必须填写货源链接'))
                return
              }
              if (url.length > 500 || (!url.startsWith('http://') && !url.startsWith('https://'))) {
                callback(new Error('货源链接须以 http:// 或 https:// 开头，且不超过 500 字'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ]
      })
}))

type ShopOption = { id: number; name: string; shopType?: number }
const shopOptions = ref<ShopOption[]>([])
/** 第三方入驻货：店铺只读 */
const shopLocked = ref(false)
const shopSelectPlaceholder = computed(() =>
  shopLocked.value ? '第三方入驻店铺（不可改）' : '请选择自营店铺'
)
const sourceUrlPlaceholder = computed(() =>
  formData.sourceChannel ? '请输入货源商品链接' : '请先选择货源渠道'
)

const onSourceChannelChange = () => {
  if (!formData.sourceChannel) {
    formData.sourceUrl = undefined
  }
  formRef.value?.validateField?.('sourceUrl')
}

/** 将传进来的值赋值给 formData */
watch(
  () => props.propFormData,
  async (data) => {
    if (!data) {
      return
    }
    copyValueToTarget(formData, data)
    syncShopLock()
  },
  {
    immediate: true
  }
)

function syncShopLock() {
  const sid = formData.shopId
  if (!sid || sid <= 0) {
    shopLocked.value = false
    return
  }
  const hit = shopOptions.value.find((s) => s.id === sid)
  if (hit) {
    shopLocked.value = hit.shopType === THIRD_PARTY
    return
  }
  // 列表尚未含当前店：先不锁，等 refreshShopList 补全后再判
  shopLocked.value = false
}

async function refreshShopList() {
  // 无自营店时自动建默认店（自营视角已无「店铺管理」入口）
  let list = (await ensureDefaultSelfOperatedShops()) as ShopOption[]
  if (!list.length) {
    const fallback = (await getShopSimpleList({ shopType: SELF_OPERATED })) as ShopOption[]
    list = (fallback || []).map((s) => ({
      id: s.id,
      name: s.name || `店铺${s.id}`,
      shopType: s.shopType ?? SELF_OPERATED
    }))
  }
  shopOptions.value = list.map((s) => ({
    id: s.id,
    name: s.name || `店铺${s.id}`,
    shopType: s.shopType ?? SELF_OPERATED
  }))
  const sid = formData.shopId
  if (sid && sid > 0 && !shopOptions.value.some((s) => s.id === sid)) {
    const all = (await getShopSimpleList()) as ShopOption[]
    const hit = all?.find((s) => s.id === sid)
    shopOptions.value = [
      ...shopOptions.value,
      {
        id: sid,
        name: hit?.name || `店铺${sid}`,
        shopType: hit?.shopType ?? THIRD_PARTY
      }
    ]
    shopLocked.value = (hit?.shopType ?? THIRD_PARTY) === THIRD_PARTY
  } else {
    syncShopLock()
  }
  // 新建发品且仅一家自营店：自动选中
  if (!shopLocked.value && (!formData.shopId || formData.shopId <= 0)) {
    const def = pickDefaultSelfOperatedShopId(shopOptions.value)
    if (def) {
      formData.shopId = def
    }
  }
}

watch(
  () => formData.shopId,
  async (sid) => {
    if (!sid || sid <= 0) {
      shopLocked.value = false
      return
    }
    if (!shopOptions.value.some((s) => s.id === sid)) {
      await refreshShopList()
    } else {
      syncShopLock()
    }
  }
)

/** 表单校验 */
const emit = defineEmits(['update:activeName'])
const validate = async () => {
  if (!formRef) return
  try {
    await unref(formRef)?.validate()
    // 校验通过更新数据
    Object.assign(props.propFormData, formData)
  } catch (e) {
    message.error('【基础设置】不完善，请填写相关信息')
    emit('update:activeName', 'info')
    throw e // 目的截断之后的校验
  }
}
defineExpose({ validate })

/** 初始化 */
const brandList = ref<BrandVO[]>([]) // 商品品牌列表
const categoryList = ref<CategoryVO[]>([]) // 商品分类树
async function refreshCategoryList() {
  // 获得分类树
  const data = await ProductCategoryApi.getCategoryList({})
  categoryList.value = handleTree(data, 'id')
}

async function refreshBrandList() {
  brandList.value = await ProductBrandApi.getSimpleBrandList()
}

onMounted(async () => {
  await refreshCategoryList()
  await refreshBrandList()
  await refreshShopList()
})
</script>
